import { Eye } from "lucide-react";
import { InputSelect } from "@/shared/components/form/InputSelect";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import type { QuoterTranslations } from "@/shared/lang/interfaces/quoter";
import { formatQuoterAmount } from "../utils/formatQuoterAmount.utils";
import type { ShippingQuote } from "../types/shipping-quotes-api.types";
import type { SelectOption } from "../hooks/useQuoterHistoryPage";

interface QuoterHistoryTableProps {
  rows: ShippingQuote[];
  totalItems: number;
  itemsPerPage: number;
  paginationOptions: SelectOption[];
  onItemsPerPageChange: (value: string) => void;
  onViewQuote?: (quote: ShippingQuote) => void;
  t: (k: keyof QuoterTranslations, opts?: Record<string, unknown>) => string;
}

export const QuoterHistoryTable = ({
  rows,
  totalItems,
  itemsPerPage,
  paginationOptions,
  onItemsPerPageChange,
  onViewQuote,
  t,
}: QuoterHistoryTableProps) => {
  const showingText =
    rows.length === 1
      ? t("history_table_showing_one", { total: totalItems })
      : t("history_table_showing_many", { count: rows.length, total: totalItems });

  return (
    <>
      <div className="px-6 py-4 border-b border-gray-200 dark:border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {showingText}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {t("history_table_show_label")}
          </span>
          <InputSelect<SelectOption>
            options={paginationOptions}
            value={
              paginationOptions.find((opt) => opt.value === String(itemsPerPage)) ??
              null
            }
            onChange={(selected) => {
              const value = Array.isArray(selected)
                ? selected[0]?.value
                : selected?.value;
              onItemsPerPageChange(value ?? "10");
            }}
            maxWidth="max-w-[120px]"
            small
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead className="bg-gray-50 dark:bg-zinc-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("history_table_th_origin")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("history_table_th_destination")}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("history_table_th_send")}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("history_table_th_receive")}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("history_table_th_commission")}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("history_table_th_rate")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("history_table_th_created_by")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("history_table_th_date")}
              </th>
              <th className="px-8 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("history_table_th_actions")}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-zinc-900 divide-y divide-gray-200 dark:divide-zinc-800">
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={9}
                  className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400"
                >
                  {t("history_table_empty")}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.id}
                  className="hover:bg-gray-50 dark:hover:bg-zinc-800/50"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {row.origin_currency}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {row.destination_currency}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-700 dark:text-gray-300">
                    {formatQuoterAmount(row.amount_to_send)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-700 dark:text-gray-300">
                    {formatQuoterAmount(row.amount_to_receive)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-700 dark:text-gray-300">
                    {row.commission}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-400">
                    {row.exchange_rate ?? "—"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 truncate max-w-[160px]">
                    {row.created_by_user?.email ?? "—"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatDateTime(row.created_at, { includeTime: true })}
                  </td>
                  <td className="px-8 py-4 whitespace-nowrap">
                    <div className="flex items-center justify-center gap-2">
                      <div className="relative group">
                        <button
                          type="button"
                          className="inline-flex items-center justify-center rounded-full p-2 border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-100 dark:hover:bg-emerald-500/25 transition-colors shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
                          aria-label={t("history_table_view_aria")}
                          onClick={() => onViewQuote?.(row)}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs font-medium text-white bg-black dark:bg-zinc-900 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                          {t("history_table_view")}
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};
